/**
 * Host-layer access state (ADR-099). It is the ONLY read side of the alpha access row and the pack
 * grants; `isMaintainer` gates only the Game codes page and its nav link and never makes a pack
 * visible. Never import this from the store, the engine or a content pack (only `usePackSelection`,
 * a host composable, does).
 *
 * State is module-level (singleton) so the router guard and every view share one load. The load is
 * memoized per signed-in user id; a user change drops the memo and the state.
 */
import { computed, ref, watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { redeemInvite, getMyAccess, type AccessRole } from '@/lib/access'

const INVITE_KEY = 'ordiria:invite'
// A waitlist invitation or a pack code is a uuid: the shape we accept before storing it.
const INVITE_RE = /^[0-9a-f-]{36}$/i

const role = ref<AccessRole | null>(null)
const packs = ref<string[]>([])
const inviteResult = ref<'ok' | 'invalid' | null>(null)

// The memoized load: `loaded` is the in-flight/done promise, `loadedFor` the user id it belongs to.
let loaded: Promise<void> | null = null
let loadedFor: string | null = null
// Bumped when the signed-in user goes away or changes, so a load started for the previous user
// writes nothing. A restored or new session (null → id) keeps an in-flight load: it reads that session.
let generation = 0
// The one in-flight (or last) redemption. Concurrent loads (AuthView's finish, AppNav's user watcher,
// the router guard) all await it, so a token is sent once: a second send found the code used and its
// `invalid` overwrote the first's `ok` (production, 2026-10-07, post-INC-187).
let redeeming: Promise<void> | null = null

/** An access holder (any role). */
export const hasAccess = computed(() => role.value !== null)
/** The maintainer: gates the Game codes page only. */
export const isMaintainer = computed(() => role.value === 'maintainer')

/** Whether the account holds its own grant for a pack. No role substitutes for it. */
export function hasPackGrant(id: string): boolean {
  return packs.value.includes(id)
}

function readInvite(): string | null {
  try {
    return localStorage.getItem(INVITE_KEY)
  } catch {
    return null
  }
}

function removeInvite() {
  try {
    localStorage.removeItem(INVITE_KEY)
  } catch {
    // Storage unavailable (private mode): nothing to remove.
  }
}

/** Redeem the stored token, if any, exactly once; otherwise wait for the redemption in flight. The
 *  key is removed before the call so no concurrent load can read and send it again. */
function redeemPending(): Promise<void> {
  const token = readInvite()
  if (token) {
    removeInvite()
    redeeming = redeemInvite(token).then((ok) => {
      inviteResult.value = ok ? 'ok' : 'invalid'
    })
  }
  return redeeming ?? Promise.resolve()
}

/** Remember a pending invitation token across the sign-up round trip. Ignores anything but a uuid. */
export function rememberInvite(token: string) {
  if (!INVITE_RE.test(token)) return
  inviteResult.value = null
  redeeming = null
  try {
    localStorage.setItem(INVITE_KEY, token)
  } catch {
    // Storage unavailable: the invite still rides the URL for this navigation.
  }
  loaded = null // a fresh load will redeem it
}

/** Load (or reuse) the current user's access row and grants. */
export function loadAccess(): Promise<void> {
  const uid = useAuth().user.value?.id ?? null
  if (loaded && loadedFor === uid) return loaded
  loadedFor = uid
  loaded = (async () => {
    const gen = generation
    const { session, ready, ensureReady } = useAuth()
    ensureReady()
    await ready
    if (gen !== generation) return
    if (!session.value?.access_token) {
      role.value = null
      packs.value = []
      return
    }
    await redeemPending() // grants read below include what this redemption added
    const a = await getMyAccess()
    if (gen !== generation) return
    role.value = a.role
    packs.value = a.packs
  })()
  return loaded
}

/** Drop the memo and reload — e.g. after revoking a grant the maintainer itself held. */
export function refreshAccess(): Promise<void> {
  loaded = null
  return loadAccess()
}

// A different signed-in user: the previous load and state no longer apply.
watch(
  () => useAuth().user.value?.id ?? null,
  (id, prev) => {
    if (id === prev) return
    loaded = null
    loadedFor = null
    if (prev !== null) {
      generation++
      role.value = null
      packs.value = []
    }
  },
)

export function useAccess() {
  return {
    role,
    packs,
    hasAccess,
    isMaintainer,
    hasPackGrant,
    inviteResult,
    rememberInvite,
    loadAccess,
    refreshAccess,
  }
}
